import React, { useEffect, useState } from 'react';
import Content from '../content';
import { classNames } from '../../helpers/classNames';
import * as styles from './Page.module.css';

interface PageProps {
  children: React.ReactNode;
  background?:
    | 'default'
    | 'riche'
    | 'wb'
    | 'ozon'
    | 'yandex'
    | 'dark'
    | 'light';
  className?: string;
}

/**
 * Главный компонент страницы - всегда full width
 *
 * @example
 * <Page>
 *   <Page.Header>
 *     <h1>Заголовок</h1>
 *     <Tabs />
 *   </Page.Header>
 *
 *   <Page.Container>
 *     <Page.Header>
 *       <Button>Фильтр</Button>
 *     </Page.Header>
 *
 *     <Page.Container fullWidth>
 *       <Table />
 *     </Page.Container>
 *   </Page.Container>
 * </Page>
 */
export default function Page({
  children,
  background = 'default',
  className = '',
}: PageProps) {
  return (
    <div className={`${styles.page} ${styles[background]} ${className}`}>
      {children}
    </div>
  );
}

// Подкомпоненты для структурирования
interface PageHeaderProps {
  children: React.ReactNode;
  container?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  className?: string;
}

function PageHeaderComponent({
  children,
  container = 'sm',
  className = '',
}: PageHeaderProps) {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      setIsScrolled(window.scrollY > 8);
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });

    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div
      className={classNames(styles.headerWrapper, {
        [styles.headerWrapperScrolled]: isScrolled,
      })}
    >
      <Content container={container}>
        <div className={`${styles.header} ${className}`}>{children}</div>
      </Content>
    </div>
  );
}

interface PageContainerProps {
  children: React.ReactNode;
  container?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  fullWidth?: boolean;
  className?: string;
}

function PageContainerComponent({
  children,
  container = 'sm',
  fullWidth = false, // По умолчанию с контейнером как у Header
  className = '',
}: PageContainerProps) {
  if (fullWidth) {
    return <>{children}</>;
  }

  return (
    <Content container={container}>
      <div className={`${styles.container} ${className}`}>{children}</div>
    </Content>
  );
}

// Экспортируем подкомпоненты
Page.Header = PageHeaderComponent;
Page.Container = PageContainerComponent;
